import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { productAttributesApi, type AttributeRecommendation, type MarketplaceAttributeMapping, type ValidationResult } from '../../api/productattributes';

export function MarketplaceAttributeMappingPage() {
  const [items, setItems] = useState<MarketplaceAttributeMapping[]>([]);
  const [attributeId, setAttributeId] = useState('');
  const [marketplace, setMarketplace] = useState('amazon');
  const [externalId, setExternalId] = useState('');
  const [error, setError] = useState('');

  const reload = () => void productAttributesApi.listMarketplaceMappings().then(setItems).catch((e) => setError(String(e)));
  useEffect(() => { reload(); }, []);

  const map = async () => {
    if (!attributeId.trim() || !externalId.trim()) return;
    try {
      await productAttributesApi.mapMarketplaceAttribute({
        attribute_id: attributeId.trim(),
        marketplace,
        external_attribute_id: externalId.trim(),
      });
      reload();
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/product-attributes" className="text-sm text-slate-600 hover:underline">← Attributes hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Marketplace Attribute Mapping</h1>
      <div className="mt-4 flex flex-wrap gap-2">
        <input value={attributeId} onChange={(e) => setAttributeId(e.target.value)} placeholder="Attribute ID" className="rounded-lg border px-3 py-2 text-sm" />
        <select value={marketplace} onChange={(e) => setMarketplace(e.target.value)} className="rounded-lg border px-3 py-2 text-sm">
          <option value="amazon">Amazon</option>
          <option value="google">Google</option>
          <option value="shopify">Shopify</option>
        </select>
        <input value={externalId} onChange={(e) => setExternalId(e.target.value)} placeholder="External attribute ID" className="rounded-lg border px-3 py-2 text-sm" />
        <button type="button" onClick={() => void map()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">Map</button>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <table className="mt-6 min-w-full rounded-xl border bg-white text-sm">
        <thead className="border-b bg-slate-50 text-left"><tr><th className="px-4 py-3">Attribute</th><th className="px-4 py-3">Marketplace</th><th className="px-4 py-3">External ID</th></tr></thead>
        <tbody>
          {items.map((m) => (
            <tr key={m.id} className="border-b">
              <td className="px-4 py-3">{m.attribute_id}</td>
              <td className="px-4 py-3">{m.marketplace}</td>
              <td className="px-4 py-3">{m.external_attribute_id}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ValidationDashboardPage() {
  const [productId, setProductId] = useState('');
  const [result, setResult] = useState<ValidationResult | null>(null);
  const [error, setError] = useState('');

  const validate = async () => {
    if (!productId.trim()) return;
    try {
      const r = await productAttributesApi.validate({ product_id: productId.trim(), values: {} });
      setResult(r);
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="mx-auto max-w-4xl p-6">
      <Link to="/product-attributes" className="text-sm text-slate-600 hover:underline">← Attributes hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Validation Dashboard</h1>
      <div className="mt-4 flex gap-2">
        <input value={productId} onChange={(e) => setProductId(e.target.value)} placeholder="Product ID" className="rounded-lg border px-3 py-2 text-sm" />
        <button type="button" onClick={() => void validate()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">Validate</button>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {result && (
        <div className="mt-6 rounded-xl border bg-white p-6">
          <p className="text-sm">Valid: <strong>{result.valid ? 'Yes' : 'No'}</strong> — Score: {(result.score * 100).toFixed(0)}%</p>
          {result.errors?.map((e, i) => <p key={i} className="mt-2 text-sm text-red-600">{e.message}</p>)}
        </div>
      )}
    </div>
  );
}

export function AIRecommendationsPage() {
  const [productId, setProductId] = useState('');
  const [productName, setProductName] = useState('');
  const [category, setCategory] = useState('');
  const [recs, setRecs] = useState<AttributeRecommendation[]>([]);
  const [error, setError] = useState('');

  const recommend = async () => {
    if (!productId.trim() || !productName.trim()) return;
    try {
      const r = await productAttributesApi.recommend({ product_id: productId.trim(), product_name: productName.trim(), category: category || undefined });
      setRecs(r);
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/product-attributes" className="text-sm text-slate-600 hover:underline">← Attributes hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">AI Attribute Recommendations</h1>
      <div className="mt-4 flex flex-wrap gap-2">
        <input value={productId} onChange={(e) => setProductId(e.target.value)} placeholder="Product ID" className="rounded-lg border px-3 py-2 text-sm" />
        <input value={productName} onChange={(e) => setProductName(e.target.value)} placeholder="Product name" className="rounded-lg border px-3 py-2 text-sm" />
        <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category" className="rounded-lg border px-3 py-2 text-sm" />
        <button type="button" onClick={() => void recommend()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">Get recommendations</button>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <table className="mt-6 min-w-full rounded-xl border bg-white text-sm">
        <thead className="border-b bg-slate-50 text-left"><tr><th className="px-4 py-3">Attribute</th><th className="px-4 py-3">Code</th><th className="px-4 py-3">Score</th><th className="px-4 py-3">Reason</th></tr></thead>
        <tbody>
          {recs.map((r) => (
            <tr key={r.attribute_id} className="border-b">
              <td className="px-4 py-3 font-medium">{r.attribute_name}</td>
              <td className="px-4 py-3">{r.code}</td>
              <td className="px-4 py-3">{(r.score * 100).toFixed(0)}%</td>
              <td className="px-4 py-3">{r.reason ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function LocalizationPage() {
  return (
    <div className="mx-auto max-w-4xl p-6">
      <Link to="/product-attributes" className="text-sm text-slate-600 hover:underline">← Attributes hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Attribute Localization</h1>
      <p className="mt-2 text-sm text-slate-600">Manage attribute translations across locales. Translations are stored per attribute via the attribute intelligence API.</p>
      <div className="mt-6 rounded-xl border bg-white p-6 text-sm text-slate-500">
        Configure locale-specific names and descriptions when editing attributes in the Attribute Builder.
      </div>
    </div>
  );
}

export function UnitsOfMeasurePage() {
  const units = [
    { code: 'cm', system: 'metric', label: 'Centimeter' },
    { code: 'm', system: 'metric', label: 'Meter' },
    { code: 'g', system: 'metric', label: 'Gram' },
    { code: 'kg', system: 'metric', label: 'Kilogram' },
    { code: 'l', system: 'metric', label: 'Liter' },
    { code: 'piece', system: 'universal', label: 'Piece' },
  ];
  return (
    <div className="mx-auto max-w-4xl p-6">
      <Link to="/product-attributes" className="text-sm text-slate-600 hover:underline">← Attributes hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Units of Measure</h1>
      <table className="mt-6 min-w-full rounded-xl border bg-white text-sm">
        <thead className="border-b bg-slate-50 text-left"><tr><th className="px-4 py-3">Code</th><th className="px-4 py-3">System</th><th className="px-4 py-3">Label</th></tr></thead>
        <tbody>
          {units.map((u) => (
            <tr key={u.code} className="border-b">
              <td className="px-4 py-3 font-mono">{u.code}</td>
              <td className="px-4 py-3">{u.system}</td>
              <td className="px-4 py-3">{u.label}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
